/**
 * The icons of a room, with no screen (item BI1): what a room's card says with a picture and a number — its beds, its
 * sofa beds, the most people it takes — and what a chosen room says once it holds a cot or a crib. Every icon carries
 * its words ("2 beds", "1 sofa bed", "Sleeps up to 4", "1 cot", "1 crib") for a screen reader and a pointer that rests on
 * it. The numbers are of beds and of people, the type's own; how many rooms a hotel has or has left is never here.
 */
import { plural } from '@/lib/format';
import type { Row } from './rooms';
import { mostPeople } from './selectRoom';

export type RoomIconKind = 'beds' | 'sofa' | 'people' | 'cot' | 'crib';
export interface RoomIcon {
  kind: RoomIconKind; count: number;
  /** The words the icon stands for. */
  text: string;
  /** Whether the number stands beside the icon. A lone cot or crib is the icon alone; its words still say "1 cot". */
  numbered: boolean;
}

/** The one number every type of a line agrees on, when it is 1 or more; null for a line whose types differ, or have none. */
function shared(values: ReadonlyArray<number | null>): number | null {
  const first = values[0] ?? null;
  return first !== null && first >= 1 && values.every((v) => v === first) ? first : null;
}

/**
 * A line's icons, in the order they stand: beds, sofa beds, people. Beds and sofa beds are shown when every type of the
 * line has the same number (a group whose types differ shows neither); no sofa icon at 0; nothing at all for a type
 * nobody has set — it has no numbers to show.
 */
export function roomIcons(row: Pick<Row, 'members'>): RoomIcon[] {
  const set = row.members.length > 0 && row.members.every((m) => m.beds.beds !== null);
  if (!set) return [];
  const beds = shared(row.members.map((m) => m.beds.beds));
  const sofas = shared(row.members.map((m) => m.sofaBeds ?? 0));
  const most = mostPeople(row);
  return [
    ...(beds === null ? [] : [{ kind: 'beds' as const, count: beds, text: plural(beds, 'bed'), numbered: true }]),
    ...(sofas === null ? [] : [{ kind: 'sofa' as const, count: sofas, text: plural(sofas, 'sofa bed'), numbered: true }]),
    ...(most === null ? [] : [{ kind: 'people' as const, count: most, text: `Sleeps up to ${most}`, numbered: true }]),
  ];
}

/** A chosen room's cot and crib icons: one each while the room holds any, with the number when it holds more than one. */
export function extraIcons(room: { cots?: number; cribs?: number }): RoomIcon[] {
  const n = (v: number | undefined) => (typeof v === 'number' && Number.isInteger(v) && v > 0 ? v : 0);
  const cots = n(room.cots);
  const cribs = n(room.cribs);
  return [
    ...(cots ? [{ kind: 'cot' as const, count: cots, text: plural(cots, 'cot'), numbered: cots > 1 }] : []),
    ...(cribs ? [{ kind: 'crib' as const, count: cribs, text: plural(cribs, 'crib'), numbered: cribs > 1 }] : []),
  ];
}
